import { useReactiveVar } from '@apollo/client';
import { ButtonV2, Card } from '@vartanainc/design-system';
import { Form, FormikProvider, useFormik } from 'formik';
import { get } from 'lodash';
import { useMemo, useState, type FC } from 'react';
import { ReactComponent as UserBadgeIcon } from '../../assets/user_badge.svg';
import AutoLoad from '../../components/AutoLoad';
import SalesManagerField from '../../components/SalesManagerSelect';
import { TEXT_CONSTANTS } from '../../constants/ui/text.constants';
import TextInputField from '../../designSystem/TextInput/TextInputField';
import { assignSalesManagerValidationSchema } from '../../formikSchema/commonSchema';
import { sessionVar } from '../../graphql/cache';
import { useUpsertUser } from './useUpsertUser.hook';

interface UserRoleType {
  id: string;
  name: string;
}

interface User {
  id: string;
  number: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string | null;
  title: string;
  updatedAt: string;
  userRole?: UserRoleType;
}

interface SelectedUserParam extends User {
  managedUsers?: User[];
  manager: User | null;
}

interface AssignSalesManagerFormProps {
  salesRepUser: SelectedUserParam;
  onCompleted: () => void;
}

interface FormValues {
  fullName: string;
  user: string; // Stores the user ID
}

const AssignSalesManagerForm: FC<AssignSalesManagerFormProps> = ({
  salesRepUser,
  onCompleted,
}): JSX.Element => {
  const { upsertUser } = useUpsertUser();
  const session = useReactiveVar(sessionVar);
  const currentUser = get(session, 'session.user');
  const currentUserRole = get(currentUser, 'userRole.name', '') as string;

  const initialFormValues: FormValues = useMemo(
    () => ({
      fullName: salesRepUser?.fullName ?? '',
      user: '', // Initialize as empty string (no selection)
    }),
    [salesRepUser]
  );

  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null); // State to store the full user object

  const handleSubmit = async (
    values: FormValues,
    { setErrors }: { setErrors: (errors: { [key: string]: string }) => void }
  ): Promise<void> => {
    setIsFormSubmitting(true);
    if (!values.user || !selectedUser) {
      setErrors({ user: 'Please select a sales manager' });
      setIsFormSubmitting(false);
      return;
    }
    const { success } = await upsertUser(salesRepUser, selectedUser, currentUserRole);

    if (success) {
      setIsFormSubmitting(false);
      onCompleted();
    }
  };

  const formikBag = useFormik({
    initialValues: initialFormValues,
    validationSchema: assignSalesManagerValidationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  // Callback to update the selected user object
  const handleUserChange = (user: User | null): void => {
    setSelectedUser(user);
  };

  return (
    <div className="p-10">
      <FormikProvider value={formikBag}>
        <Form className="create-user-container">
          <Card
            tabindex={0}
            title="Assign sales manager"
            icon={<UserBadgeIcon />}
            content={(
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <TextInputField
                      {...{
                        name: 'fullName',
                        label: 'Sales rep',
                        fullWidth: 'true',
                        disabled: true,
                      }}
                      ref={null}
                    />
                  </div>
                </div>

                <SalesManagerField
                  name="user"
                  label={TEXT_CONSTANTS.SALES_MANAGER_LABEL}
                  placeholder="Select"
                  onUserChange={handleUserChange}
                />

                <div className="flex flex-row justify-between mt-8">
                  <ButtonV2
                    type="button"
                    text="Cancel"
                    iconLeft="chevron_left"
                    variant={{ type: 'ghost', typography: 'paragraph14' }}
                    onClick={onCompleted}
                  />

                  <AutoLoad loading={isFormSubmitting}>
                    <ButtonV2
                      type="submit"
                      disabled={!formikBag.isValid || !formikBag.dirty}
                      text="Submit"
                      variant={{ type: 'primary', typography: 'paragraph14' }}
                    />
                  </AutoLoad>
                </div>
              </>
            )}
          />
        </Form>
      </FormikProvider>
    </div>
  );
};

export default AssignSalesManagerForm;
